import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import {
  filterRespondItemsByMitra,
  mapOwnResponsesByClauseId,
  normalizeRespondClauseDetail,
  unwrapRespondListItems,
} from './helpers';
import { buildRespondClauseListUrl } from './listParams';
import { unwrapPayload } from '../../api/unwrap';

const parseResponseItems = payload => {
  const list = unwrapRespondListItems(payload)
    .map(normalizeRespondClauseDetail)
    .filter(Boolean);

  if (list.length > 0) return list;

  const single = normalizeRespondClauseDetail(unwrapPayload(payload));
  return single ? [single] : [];
};

const useClauseDetailResponses = (
  authToken = null,
  procurementMitraId = null,
  procurementClauseDetailIds = [],
  procurementId = null
) => {
  const [clauseResponses, setClauseResponses] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fetchSeqRef = useRef(0);

  const clauseDetailIdsKey = useMemo(
    () =>
      [...new Set((procurementClauseDetailIds ?? []).filter(Boolean))]
        .sort()
        .join(','),
    [procurementClauseDetailIds]
  );

  const clauseDetailIds = useMemo(() => {
    if (!clauseDetailIdsKey) return [];
    return clauseDetailIdsKey.split(',').filter(Boolean);
  }, [clauseDetailIdsKey]);

  const fetchResponses = useCallback(async () => {
    const token = authToken || getAuthToken();
    if (
      !token ||
      !procurementId ||
      !procurementMitraId ||
      clauseDetailIds.length === 0
    ) {
      setClauseResponses({});
      setIsLoading(false);
      return;
    }

    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setIsLoading(true);
    try {
      const response = await axios.get(
        buildRespondClauseListUrl({
          procurementId,
          procurementMitraId,
        }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (fetchSeqRef.current !== seq) return;

      const clauseIdSet = new Set(clauseDetailIds.map(String));
      const items = filterRespondItemsByMitra(
        parseResponseItems(response),
        procurementMitraId,
        { scopedQuery: true }
      ).filter(item => clauseIdSet.has(String(item.procurementClauseDetailId)));

      setClauseResponses(mapOwnResponsesByClauseId(items));
    } catch (error) {
      if (fetchSeqRef.current !== seq) return;
      if (error?.response?.status === 404) {
        setClauseResponses({});
        return;
      }
      setClauseResponses({});
    } finally {
      if (fetchSeqRef.current === seq) setIsLoading(false);
    }
  }, [authToken, clauseDetailIds, procurementId, procurementMitraId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const saveResponse = useCallback(
    async (procurementClauseDetailId, respondNotes, options = {}) => {
      const token = authToken || getAuthToken();
      if (!token || !procurementMitraId || !procurementClauseDetailId) {
        return null;
      }

      const existing = clauseResponses[procurementClauseDetailId];
      const notes = String(respondNotes ?? existing?.respondNotes ?? '').trim();
      const isConfirmed =
        options.isConfirmed === true ||
        existing?.isConfirmed === true ||
        Boolean(notes);
      const payload = {
        procurementClauseDetailId,
        respondNotes: notes,
        isConfirmed,
      };

      setIsSaving(true);
      try {
        const response = existing?.id
          ? await axios.put(
              `${api.API_URL}/respond-clause-detail/${existing.id}`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            )
          : await axios.post(`${api.API_URL}/respond-clause-detail`, payload, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });

        const saved = normalizeRespondClauseDetail(unwrapPayload(response));

        if (saved) {
          setClauseResponses(prev => ({
            ...prev,
            [procurementClauseDetailId]: saved,
          }));
          return saved;
        }

        await fetchResponses();
        return null;
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, clauseResponses, fetchResponses, procurementMitraId]
  );

  const confirmResponse = useCallback(
    async procurementClauseDetailId => {
      const existing = clauseResponses[procurementClauseDetailId];
      if (existing?.isConfirmed === true) return existing;
      return saveResponse(
        procurementClauseDetailId,
        existing?.respondNotes ?? '',
        {
          isConfirmed: true,
        }
      );
    },
    [clauseResponses, saveResponse]
  );

  const confirmResponses = useCallback(
    async procurementClauseDetailIds => {
      const ids = [
        ...new Set((procurementClauseDetailIds ?? []).filter(Boolean)),
      ];
      const results = [];
      for (const clauseId of ids) {
        const existing = clauseResponses[clauseId];
        if (existing?.isConfirmed === true) {
          results.push(existing);
          continue;
        }
        const saved = await saveResponse(
          clauseId,
          existing?.respondNotes ?? '',
          { isConfirmed: true }
        );
        results.push(saved);
      }
      return results;
    },
    [clauseResponses, saveResponse]
  );

  const deleteResponse = useCallback(
    async procurementClauseDetailId => {
      const token = authToken || getAuthToken();
      const existing = clauseResponses[procurementClauseDetailId];

      if (!token || !existing?.id) {
        return;
      }

      setIsSaving(true);
      try {
        await axios.delete(
          `${api.API_URL}/respond-clause-detail/${existing.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setClauseResponses(prev => {
          const next = { ...prev };
          delete next[procurementClauseDetailId];
          return next;
        });
      } catch (error) {
        if (error?.response?.status === 409) {
          await fetchResponses();
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [authToken, clauseResponses, fetchResponses]
  );

  return {
    clauseResponses,
    isLoading,
    isSaving,
    refresh: fetchResponses,
    saveResponse,
    confirmResponse,
    confirmResponses,
    deleteResponse,
  };
};

export default useClauseDetailResponses;
